import { getCohortSummary } from './cohortSheetSummary';
import {
  computeUaPredictionFromHill,
  resolveLatestCohortMarketingSpend,
  resolveUaPredictionInputs,
} from './uaPrediction';

export function calcCacPaybackMonth1Indexed(
  genericRoasVectorPct,
  thresholdPct = 100
) {
  if (!genericRoasVectorPct?.length) {
    return null;
  }
  for (let mob = 0; mob < genericRoasVectorPct.length; mob += 1) {
    const roasPct = Number(genericRoasVectorPct[mob]) || 0;
    if (roasPct >= thresholdPct) {
      return mob + 1;
    }
  }
  return null;
}

export function calcRoasVectorMonth1Indexed(genericRoasVectorPct, thresholdPct = 50) {
  return calcCacPaybackMonth1Indexed(genericRoasVectorPct, thresholdPct);
}

export function calcBalanceSheetRatio(totalAssets, totalLiabilities, safe = 0) {
  const assets = Number(totalAssets);
  const liabilities = Number(totalLiabilities);
  const safeAmount = Number(safe) || 0;
  const adjustedLiabilities = liabilities - safeAmount;
  if (
    !Number.isFinite(assets) ||
    !Number.isFinite(adjustedLiabilities) ||
    adjustedLiabilities <= 0
  ) {
    return null;
  }
  return assets / adjustedLiabilities;
}

export function resolveSafeAmount(safeRaw) {
  if (safeRaw == null) {
    return 0;
  }
  if (typeof safeRaw === 'object') {
    const amount = safeRaw.amount;
    if (amount != null && Number.isFinite(Number(amount))) {
      return Number(amount);
    }
    return 0;
  }
  const parsed = Number(safeRaw);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function calcRunoffOpexRatio(runoff12Months, monthlyOpex) {
  const opex = Number(monthlyOpex) || 0;
  if (opex <= 0) {
    return null;
  }
  const runoff = Number(runoff12Months) || 0;
  return runoff / opex;
}

export function calcCashOpexMultiple(beginCash, monthlyOpex) {
  const opex = Number(monthlyOpex) || 0;
  if (opex <= 0) {
    return null;
  }
  return (Number(beginCash) || 0) / opex;
}

export function calcRunwayMonthsApprox(beginCash, monthlyOpex) {
  return calcCashOpexMultiple(beginCash, monthlyOpex);
}

export function roundRunwayMonths(months) {
  if (months == null || !Number.isFinite(Number(months))) {
    return null;
  }
  return Math.round(Number(months) * 100) / 100;
}

export function calcRunwayFromNetBurn(beginCash, netBurn) {
  const burn = Math.abs(Number(netBurn) || 0);
  const cash = Number(beginCash) || 0;
  if (burn <= 0 || !Number.isFinite(burn) || !Number.isFinite(cash)) {
    return null;
  }
  return roundRunwayMonths(cash / burn);
}

export function calcNetBurnAbsolute(netBurn) {
  const value = Number(netBurn);
  if (!Number.isFinite(value)) {
    return null;
  }
  return Math.abs(value);
}

export function resolveModelNetBurn(netBurn) {
  const value = Number(netBurn);
  if (!Number.isFinite(value) || value === 0) {
    return null;
  }
  return calcNetBurnAbsolute(value);
}

export function isManualUwSource(source) {
  return source === 'Manual entry';
}

export function resolveManualUwNumber(value, source) {
  if (!isManualUwSource(source)) {
    return null;
  }
  if (value == null || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function snapshotFieldValue(field) {
  const value = field?.value;
  return value != null && Number.isFinite(Number(value)) ? Number(value) : null;
}

/** Manual UW override wins over extract snapshot, then positive model state. */
export function resolveBalanceSheetAmount(modelValue, source, snapshotField) {
  const fromManual = resolveManualUwNumber(modelValue, source);
  if (fromManual != null) {
    return fromManual;
  }
  const fromSnapshot = snapshotFieldValue(snapshotField);
  if (fromSnapshot != null) {
    return fromSnapshot;
  }
  const parsed = Number(modelValue);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function calcLiquidValueOpexMultiple(
  beginCash,
  runoff12Months,
  debt,
  monthlyOpex
) {
  const opex = Number(monthlyOpex) || 0;
  if (opex <= 0) {
    return null;
  }
  const liquidAssets =
    (Number(beginCash) || 0) +
    (Number(runoff12Months) || 0) -
    (Number(debt) || 0);
  return liquidAssets / opex;
}

export function formatOpexMultiple(ratio) {
  if (ratio == null || !Number.isFinite(ratio)) {
    return null;
  }
  return `~${ratio.toFixed(1)}x OPEX`;
}

export function formatRunoffOpexRatio(ratio) {
  return formatOpexMultiple(ratio);
}

export function formatRunwayMonths(months) {
  const rounded = roundRunwayMonths(months);
  if (rounded == null) {
    return null;
  }
  return `~${rounded.toFixed(2)} mo`;
}

export function formatCacPaybackMonths(month) {
  if (month == null || !Number.isFinite(month)) {
    return null;
  }
  return `~${Math.round(month)} mo`;
}

export function getUnderwritingNumericInputs({
  cohortSheet,
  monthlyOpex,
  beginCash = 0,
  debt = 0,
  totalAssets = 0,
  totalAssetsSource = 'Default',
  totalLiabilities = 0,
  totalLiabilitiesSource = 'Default',
  safe = 0,
  netBurn = 0,
  runway = null,
  runwaySource = 'Default',
  predictedRunoff = null,
  predictedRunoffSource = 'Default',
  liquidValue = null,
  liquidValueSource = 'Default',
  cashBalance = null,
  cashBalanceSource = 'Default',
  balanceSheet = null,
  balanceSheetSource = 'Default',
  cacPredictionRatio = null,
  cacPredictionRatioSource = 'Default',
  revenueGrowthYoy = null,
  revenueGrowthYoySource = 'Default',
  hillModel = null,
  lastMarketingSpend = 0,
  maxMarketingRoas100 = 0,
  accountingSnapshot = null,
}) {
  const summary = getCohortSummary(cohortSheet);
  const opex = Number(monthlyOpex) || 0;

  const snapshotValue = snapshotFieldValue;

  const cashBalanceFromSnapshot = snapshotValue(accountingSnapshot?.cashBalance);
  const revenueGrowthFromSnapshot = snapshotValue(accountingSnapshot?.revenueGrowthYoy);
  const hasExplicitRevenueGrowthYoy =
    revenueGrowthYoySource != null && revenueGrowthYoySource !== 'Default';
  const revenueGrowthFromModel =
    hasExplicitRevenueGrowthYoy &&
    revenueGrowthYoy != null &&
    Number.isFinite(Number(revenueGrowthYoy))
      ? Number(revenueGrowthYoy)
      : null;
  const balanceSheetFromSnapshot = snapshotValue(accountingSnapshot?.balanceSheet);
  const netBurnFromSnapshot = snapshotValue(accountingSnapshot?.netBurn);
  const resolvedBeginCash = Number(beginCash) || 0;
  const netBurnAbsolute =
    resolveModelNetBurn(netBurn) ??
    (netBurnFromSnapshot != null ? calcNetBurnAbsolute(netBurnFromSnapshot) : null);
  const runwayFromNetBurn = calcRunwayFromNetBurn(resolvedBeginCash, netBurnAbsolute);
  const runwayFromManual = roundRunwayMonths(
    resolveManualUwNumber(runway, runwaySource)
  );
  const runwayFromSnapshot = roundRunwayMonths(
    snapshotValue(accountingSnapshot?.runway)
  );
  const predictedRunoffFromManual = resolveManualUwNumber(
    predictedRunoff,
    predictedRunoffSource
  );
  const resolvedPredictedRunoff12 =
    predictedRunoffFromManual ?? summary.runoff12Months;

  const liquidFromManual = resolveManualUwNumber(liquidValue, liquidValueSource);
  const cashFromManual = resolveManualUwNumber(cashBalance, cashBalanceSource);
  const balanceFromManual = resolveManualUwNumber(balanceSheet, balanceSheetSource);
  const computedLiquidValue =
    opex > 0
      ? calcLiquidValueOpexMultiple(
          beginCash,
          resolvedPredictedRunoff12,
          debt,
          opex
        )
      : null;

  const uaPrediction = resolveUaPredictionInputs({
    cohortSheet,
    hillModel,
    lastMarketingSpend,
    maxMarketingRoas100,
  });
  const cacFromManual = resolveManualUwNumber(
    cacPredictionRatio,
    cacPredictionRatioSource
  );

  const resolvedAssets = resolveBalanceSheetAmount(
    totalAssets,
    totalAssetsSource,
    accountingSnapshot?.totalAssets
  );
  const resolvedLiabilities = resolveBalanceSheetAmount(
    totalLiabilities,
    totalLiabilitiesSource,
    accountingSnapshot?.totalLiabilities
  );
  const resolvedSafe =
    snapshotValue(accountingSnapshot?.safe) ??
    resolveSafeAmount(safe);
  const computedBalanceSheetRatio = calcBalanceSheetRatio(
    resolvedAssets,
    resolvedLiabilities,
    resolvedSafe
  );

  const inputs = {
    runway: runwayFromManual ?? runwayFromNetBurn ?? runwayFromSnapshot ?? null,
    net_burn: netBurnAbsolute,
    predicted_runoff_12_months: resolvedPredictedRunoff12,
    runoff_without_cash:
      opex > 0 ? calcRunoffOpexRatio(resolvedPredictedRunoff12, opex) : null,
    liquid_value: liquidFromManual ?? computedLiquidValue,
    revenue_growth_yoy: revenueGrowthFromModel ?? revenueGrowthFromSnapshot,
    total_assets: resolvedAssets,
    total_liabilities: resolvedLiabilities,
    balance_sheet: balanceFromManual ?? computedBalanceSheetRatio ?? balanceSheetFromSnapshot,
    stress_testing: null,
    cash_balance:
      cashFromManual ??
      (opex > 0 ? calcCashOpexMultiple(beginCash, opex) : null) ??
      cashBalanceFromSnapshot,
    cac_payback: calcCacPaybackMonth1Indexed(summary.genericRoasVectorPct, 100),
    roas_vector: calcRoasVectorMonth1Indexed(summary.genericRoasVectorPct, 50),
    ua_prediction: cacFromManual ?? uaPrediction.ratio ?? null,
    ua_prediction_detail: uaPrediction,
    investors: null,
    management: null,
  };

  return inputs;
}

/** @deprecated use getUnderwritingNumericInputs + buildUwScoringRows */
export function getUnderwritingCalculatedValues(cohortSheet, monthlyOpex) {
  const summary = getCohortSummary(cohortSheet);
  const runoffRatio = calcRunoffOpexRatio(
    summary.runoff12Months,
    monthlyOpex
  );
  const cacPaybackMonth = calcCacPaybackMonth1Indexed(
    summary.genericRoasVectorPct
  );

  return {
    runoffEstimatedValue: formatRunoffOpexRatio(runoffRatio),
    cacPaybackEstimatedValue: formatCacPaybackMonths(cacPaybackMonth),
    monthlyOpex:
      monthlyOpex != null && Number(monthlyOpex) > 0
        ? Number(monthlyOpex)
        : null,
  };
}
